import type { TailRecord, EngagementStatus } from '@/domain/types';

/**
 * Nurture cadence buckets (Prompt K, K4) - lists only. Every past customer sorts
 * into exactly one contact cadence, derived from the engagement status that
 * already drives the nurture lists. `EngagementStatus` is a closed four-value
 * union and the field is required, so the partition is deterministic and total -
 * no gaps, no overlaps. `doNotContact` customers are forced into the dead pile
 * regardless of status. Read-only: no reminders, no sending.
 */

export type NurtureCadence = 'monthly' | 'quarterly' | 'yearly' | 'dead-pile';

export interface NurtureBucket {
  cadence: NurtureCadence;
  label: string;
  /** One-line description of who lands here. */
  blurb: string;
  records: TailRecord[];
}

const ORDER: { cadence: NurtureCadence; label: string; blurb: string }[] = [
  { cadence: 'monthly', label: 'Monthly', blurb: 'Active · seen recently' },
  { cadence: 'quarterly', label: 'Quarterly', blurb: 'Warm · a few months back' },
  { cadence: 'yearly', label: 'Yearly', blurb: 'Lapsed · 6–18 months' },
  { cadence: 'dead-pile', label: 'Dead pile', blurb: 'Dormant · 18 months+ · incl. do-not-contact' },
];

const BY_STATUS: Record<EngagementStatus, NurtureCadence> = {
  active: 'monthly',
  warm: 'quarterly',
  lapsed: 'yearly',
  dormant: 'dead-pile',
};

function cadenceFor(record: TailRecord): NurtureCadence {
  if (record.doNotContact) return 'dead-pile';
  return BY_STATUS[record.engagementStatus];
}

/** Partition tail records into the four cadence buckets, each sorted most-recent first. */
export function nurtureBuckets(records: readonly TailRecord[]): NurtureBucket[] {
  const grouped: Record<NurtureCadence, TailRecord[]> = {
    monthly: [],
    quarterly: [],
    yearly: [],
    'dead-pile': [],
  };
  for (const r of records) grouped[cadenceFor(r)].push(r);
  for (const key of Object.keys(grouped) as NurtureCadence[]) {
    grouped[key].sort((a, b) => b.lastInteractionAt.localeCompare(a.lastInteractionAt));
  }
  return ORDER.map(({ cadence, label, blurb }) => ({ cadence, label, blurb, records: grouped[cadence] }));
}
